import axios from 'axios'
import { useKBar, useRegisterActions, Priority, Action } from 'kbar'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import api from 'utils/api'

const searchId = 'brandSearch'

const getBrandName = (brand: { id: number; name: string; role: string }) => {
  return (
    <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
      <Typography variant="subtitle2">{brand.name}</Typography>
      <Chip
        label={brand.role}
        size="small"
        sx={{
          fontSize: '11px',
          height: '100%',
          backgroundColor: theme => theme.palette.primary.main,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: theme => theme.palette.neutral[500],
          borderRadius: '4px',
          '& .MuiChip-label': {
            px: '4px',
            py: 0,
          },
        }}
      />
    </Box>
  )
}

export function useBrandSearchActions() {
  const { searchQuery } = useKBar(state => ({
    searchQuery: state.searchQuery,
  }))
  const [brands, setBrands] = useState([])
  const router = useRouter()

  useEffect(() => {
    let CancelToken = axios.CancelToken
    let source = CancelToken.source()
    const handleSearch = async () => {
      try {
        const response = await api.get(
          `/branding?fields=id,name,role,status&pagination%5Bpage%5D=0&pagination%5Blimit%5D=10&searchText=${searchQuery}`,
          {
            cancelToken: source.token,
          },
        )

        setBrands(response.data.data || [])
      } catch (err) {
        console.error(err)
        setBrands([])
      }
    }

    if (searchQuery) {
      handleSearch()
    } else {
      setBrands([])
    }

    return () => {
      source.cancel('Cancelled due to stale request')
    }
  }, [searchQuery])

  const createAction = (brand: {
    id: number
    name: string
    role: string
    status: string
  }) => ({
    id: `brand-${brand.id}`,
    parent: searchId,
    name: getBrandName(brand),
    subtitle: (
      <Typography color="textSecondary" variant="body2">
        {brand.status}
      </Typography>
    ),
    section: 'Brands',
    keywords: [brand.id.toString(), brand.name, brand.role],
    perform: () => {
      router.push(`/brands/${brand.id}?tab=details`)
    },
  })

  const rootSearchAction = useMemo(
    () => ({
      id: searchId,
      shortcut: ['b'],
      keywords: 'find search brands brandings',
      section: 'Search',
      priority: Priority.HIGH,
      name: 'Search Brands',
      hideBreadcrumbs: true,
    }),
    [],
  )

  const actions = useMemo(() => {
    if (!brands.length) return [rootSearchAction]
    return [rootSearchAction, ...brands.map(brand => createAction(brand))]
  }, [brands, rootSearchAction])

  useRegisterActions(actions as Action[], [actions])
}
